import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Smartphone, ShieldCheck, Copy, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { API } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useJazyk } from '../../context/LanguageContext';
import { otiskHesla, jePodporovano } from '../../lib/crypto';

/**
 * Dvoufázové přihlášení jednorázovým kódem z aplikace v telefonu.
 *
 * Funguje s Google Authenticatorem, Authy, 1Password, Aegis i s čímkoli dalším,
 * co umí TOTP — Bloom se s žádnou z nich nedomlouvá a žádnou nezvýhodňuje.
 *
 * V rozhraní je schválně napsané, co to nechrání. Klíč k šifrovaným zprávám se
 * odemyká heslem přímo v prohlížeči a server u toho není, takže 2FA na ně nemá
 * vliv. Falešný pocit bezpečí je horší než žádný — podle něj se lidé rozhodují,
 * co si napíšou.
 */
export default function TwoFactorPanel() {
  const { user } = useAuth();
  const emailUctu = user?.email || '';
  const { t } = useJazyk();
  const [stav, setStav] = useState(null);
  const [priprava, setPriprava] = useState(null);   // { secret, qr_svg }
  const [kod, setKod] = useState('');
  const [zalozni, setZalozni] = useState(null);     // ukazují se jen jednou
  const [heslo, setHeslo] = useState('');
  const [vypina, setVypina] = useState(false);
  const [pracuje, setPracuje] = useState(false);

  const nactiStav = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`);
      setStav(data);
    } catch { setStav({ enabled: false }); }
  }, []);

  useEffect(() => { nactiStav(); }, [nactiStav]);

  const zacni = async () => {
    setPracuje(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, {});
      setPriprava(data);
      setKod('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('dvf.nepovedlo'));
    } finally { setPracuje(false); }
  };

  const potvrd = async (e) => {
    e.preventDefault();
    setPracuje(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/confirm`, { code: kod });
      setZalozni(data.recovery_codes);
      setPriprava(null);
      setKod('');
      nactiStav();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('dvf.kodNesedi'));
    } finally { setPracuje(false); }
  };

  const noveKody = async () => {
    setPracuje(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/recovery/new`, { code: kod });
      setZalozni(data.recovery_codes);
      setKod('');
      nactiStav();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('dvf.kodNesedi'));
    } finally { setPracuje(false); }
  };

  const vypni = async (e) => {
    e.preventDefault();
    setPracuje(true);
    try {
      // Server hesla nedostává. U převedeného účtu je i tady potřeba poslat
      // otisk — ověřuje se proti témuž, co se ukládá při přihlášení.
      const { data: solInfo } = await axios.get(`${API}/auth/login-salt`, {
        params: { email: emailUctu },
      });
      const doklad = (solInfo.auth_version === 2 && jePodporovano())
        ? { auth_proof: await otiskHesla(heslo, solInfo.auth_salt) }
        : { password: heslo };

      await axios.post(`${API}/auth/2fa/disable`, { ...doklad, code: kod });
      toast.success(t('dvf.vypnuto2'));
      setVypina(false); setHeslo(''); setKod(''); setZalozni(null);
      nactiStav();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('dvf.nepovedlo'));
    } finally { setPracuje(false); }
  };

  const zkopiruj = () => {
    navigator.clipboard?.writeText((zalozni || []).join('\n'))
      .then(() => toast.success(t('dvf.zkopirovano')))
      .catch(() => toast.error(t('dvf.kopieChyba')));
  };

  if (!stav) return null;

  return (
    <div className="border-t border-border/50 pt-4 space-y-3" data-testid="dvoufazove">
      <div className="flex items-start gap-2.5 text-sm">
        {stav.enabled
          ? <ShieldCheck className="w-4 h-4 text-bloom-mint shrink-0 mt-0.5" />
          : <Smartphone className="w-4 h-4 text-bloom-sub shrink-0 mt-0.5" />}
        <div className="flex-1">
          <p className="font-medium text-bloom-text">{t('dvf.titulek')}</p>
          <p className="text-xs text-bloom-sub mt-0.5">
            {stav.enabled
              ? t('dvf.zapnuto', { pocet: stav.recovery_left })
              : t('dvf.vypnuto')}
          </p>
        </div>
      </div>

      {/* Co to umí a co ne. Bez tohohle by si člověk mohl myslet, že mu 2FA
          chrání i zprávy — a podle toho se rozhodovat, co napíše. */}
      <p className="text-xs text-bloom-sub bg-bloom-bg border border-border/50 rounded-lg px-3 py-2 leading-relaxed">
        {t('dvf.chraniA')} <strong>{t('dvf.chraniPrihlaseni')}</strong>{t('dvf.chraniB')}{' '}
        <strong>{t('dvf.nechrani')}</strong>{t('dvf.nechraniPopis')}
      </p>

      {/* Záložní kódy — ukazují se jedinkrát */}
      {zalozni && (
        <div className="rounded-xl border border-amber-200/70 bg-amber-50 p-3" data-testid="zalozni-kody">
          <p className="text-sm font-medium text-amber-900">
            {t('dvf.ulozKody')}
          </p>
          <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
            {t('dvf.ulozKodyPopis1')} <strong>{t('dvf.znovuNeukaze')}</strong>{t('dvf.ulozKodyPopis2')}
          </p>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 font-mono text-sm text-amber-900">
            {zalozni.map((k) => <span key={k}>{k}</span>)}
          </div>
          <div className="mt-2.5 flex gap-2">
            <Button size="sm" variant="outline" onClick={zkopiruj}>
              <Copy className="w-3.5 h-3.5 mr-1.5" />{t('dvf.zkopirovat')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setZalozni(null)}>
              {t('dvf.ulozilaJsem')}
            </Button>
          </div>
        </div>
      )}

      {/* Zapínání */}
      {!stav.enabled && !priprava && !zalozni && (
        <Button variant="outline" onClick={zacni} disabled={pracuje} data-testid="zapnout-2fa">
          {pracuje ? t('dvf.pripravuji') : t('dvf.zapnout')}
        </Button>
      )}

      {priprava && (
        <form onSubmit={potvrd} className="rounded-xl border border-border/50 p-3 space-y-3">
          <p className="text-sm text-bloom-text">
            {t('dvf.nactiKod')}
          </p>
          <div className="bg-white rounded-lg p-2 w-fit border border-border/40 [&>svg]:w-40 [&>svg]:h-40"
               // QR kód přichází ze serveru jako obrázek. Není to text od
               // uživatele a nikam se neukládá — jen se vykreslí.
               dangerouslySetInnerHTML={{ __html: priprava.qr_svg }} />
          <details className="text-xs text-bloom-sub">
            <summary className="cursor-pointer">{t('dvf.qrNejde')}</summary>
            <p className="mt-1.5">{t('dvf.zadejRucne')}</p>
            <code className="mt-1 block font-mono text-[11px] break-all text-bloom-text">
              {priprava.secret}
            </code>
          </details>
          <div>
            <Label className="text-sm">{t('dvf.opisKod')}</Label>
            <Input value={kod} onChange={(e) => setKod(e.target.value)}
                   placeholder="123456" inputMode="numeric" autoComplete="one-time-code"
                   className="mt-1" data-testid="potvrzovaci-kod" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={pracuje}
                    className="bg-bloom-violet hover:bg-bloom-violet/90" data-testid="potvrdit-2fa">
              {pracuje ? t('dvf.overuji') : t('dvf.zapnoutKratce')}
            </Button>
            <Button type="button" variant="outline" onClick={() => setPriprava(null)}>
              {t('obecne.zrusit')}
            </Button>
          </div>
        </form>
      )}

      {/* Když je zapnuté */}
      {stav.enabled && !zalozni && (
        <div className="flex flex-wrap gap-2">
          {!vypina && (
            <>
              <Button variant="outline" size="sm" onClick={() => setVypina('kody')}>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />{t('dvf.noveKody')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setVypina('vypnout')}
                      className="text-red-600 border-red-200 hover:bg-red-50">
                {t('dvf.vypnout')}
              </Button>
            </>
          )}
        </div>
      )}

      {vypina === 'kody' && (
        <div className="rounded-xl border border-border/50 p-3 space-y-2">
          <p className="text-sm text-bloom-text">
            {t('dvf.noveKodyPopis')}
          </p>
          <Label className="text-sm">{t('dvf.kodZAplikace')}</Label>
          <Input value={kod} onChange={(e) => setKod(e.target.value)}
                 placeholder="123456" inputMode="numeric" />
          <div className="flex gap-2">
            <Button size="sm" onClick={noveKody} disabled={pracuje}
                    className="bg-bloom-violet hover:bg-bloom-violet/90">
              {pracuje ? t('dvf.overuji') : t('dvf.vygenerovat')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setVypina(false); setKod(''); }}>
              {t('obecne.zrusit')}
            </Button>
          </div>
        </div>
      )}

      {vypina === 'vypnout' && (
        <form onSubmit={vypni} className="rounded-xl border border-red-200 bg-red-50/50 p-3 space-y-2">
          <p className="text-sm text-bloom-text">
            {t('dvf.vypnutiPopis')}
          </p>
          <div>
            <Label className="text-sm">{t('auth.heslo')}</Label>
            <Input type="password" value={heslo} onChange={(e) => setHeslo(e.target.value)}
                   autoComplete="current-password" className="mt-1" />
          </div>
          <div>
            <Label className="text-sm">{t('dvf.kodNeboZalozni')}</Label>
            <Input value={kod} onChange={(e) => setKod(e.target.value)}
                   className="mt-1" data-testid="vypnout-kod" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" variant="outline" disabled={pracuje}
                    className="text-red-600 border-red-300 hover:bg-red-50">
              {pracuje ? t('dvf.vypinam') : t('dvf.vypnoutCele')}
            </Button>
            <Button type="button" size="sm" variant="outline"
                    onClick={() => { setVypina(false); setHeslo(''); setKod(''); }}>
              {t('obecne.zrusit')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
